import {
  LiveChatProvider,
  useArticle,
  useArticleSuggestions,
  useCategoryArticles,
  useConversation,
  useConversations,
  useHelpHome,
  useHelpSearch,
  useLiveChatClient,
  useLiveChatState,
  useMessenger,
  useTranslate,
  useUnreadCount,
  useWorkspaceConfig
} from "./chunk-AQOTEOJQ.js";

// src/web/Messenger.tsx
import { useEffect as useEffect2, useInsertionEffect, useRef as useRef2 } from "react";

// src/web/icons.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var base = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", viewBox: "0 0 24 24", "aria-hidden": true };
var ChatIcon = (p) => /* @__PURE__ */ jsx("svg", { ...base, ...p, children: /* @__PURE__ */ jsx("path", { d: "M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12Z" }) });
var CloseIcon = (p) => /* @__PURE__ */ jsx("svg", { ...base, ...p, children: /* @__PURE__ */ jsx("path", { d: "M18 6 6 18M6 6l12 12" }) });
var BackIcon = (p) => /* @__PURE__ */ jsx("svg", { ...base, ...p, children: /* @__PURE__ */ jsx("path", { d: "m15 18-6-6 6-6" }) });
var ChevronIcon = (p) => /* @__PURE__ */ jsx("svg", { ...base, ...p, children: /* @__PURE__ */ jsx("path", { d: "m9 18 6-6-6-6" }) });
var SearchIcon = (p) => /* @__PURE__ */ jsxs("svg", { ...base, ...p, children: [
  /* @__PURE__ */ jsx("circle", { cx: "11", cy: "11", r: "7" }),
  /* @__PURE__ */ jsx("path", { d: "m20 20-3.5-3.5" })
] });
var SendIcon = (p) => /* @__PURE__ */ jsx("svg", { ...base, ...p, children: /* @__PURE__ */ jsx("path", { d: "M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z" }) });
var PaperclipIcon = (p) => /* @__PURE__ */ jsx("svg", { ...base, ...p, children: /* @__PURE__ */ jsx("path", { d: "m21.4 11.1-9.2 9.2a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 0 1-2.8-2.8l8.5-8.5" }) });
var FileIcon = (p) => /* @__PURE__ */ jsxs("svg", { ...base, ...p, children: [
  /* @__PURE__ */ jsx("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" }),
  /* @__PURE__ */ jsx("path", { d: "M14 2v6h6" })
] });
var ArticleIcon = (p) => /* @__PURE__ */ jsxs("svg", { ...base, ...p, children: [
  /* @__PURE__ */ jsx("path", { d: "M4 4h16v16H4z" }),
  /* @__PURE__ */ jsx("path", { d: "M8 9h8M8 13h8M8 17h5" })
] });

// src/web/markdown.tsx
import { parseMarkdown } from "@kobecuppens/livechat-core";
import { Fragment, useMemo } from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
function renderInline(nodes, key = "") {
  return nodes.map((n, i) => {
    const k = `${key}${i}`;
    switch (n.type) {
      case "text":
        return /* @__PURE__ */ jsx2(Fragment, { children: n.text }, k);
      case "strong":
        return /* @__PURE__ */ jsx2("strong", { children: renderInline(n.children, k) }, k);
      case "em":
        return /* @__PURE__ */ jsx2("em", { children: renderInline(n.children, k) }, k);
      case "code":
        return /* @__PURE__ */ jsx2("code", { children: n.text }, k);
      case "break":
        return /* @__PURE__ */ jsx2("br", {}, k);
      case "image":
        return /* @__PURE__ */ jsx2("img", { src: n.src, alt: n.alt, loading: "lazy" }, k);
      case "link":
        return /* @__PURE__ */ jsx2("a", { href: n.href, target: "_blank", rel: "noopener noreferrer nofollow", children: renderInline(n.children, k) }, k);
    }
  });
}
function renderBlock(b, i) {
  switch (b.type) {
    case "heading": {
      const H = `h${Math.min(b.level + 1, 4)}`;
      return /* @__PURE__ */ jsx2(H, { children: renderInline(b.children) }, i);
    }
    case "paragraph":
      return /* @__PURE__ */ jsx2("p", { children: renderInline(b.children) }, i);
    case "list": {
      const L = b.ordered ? "ol" : "ul";
      return /* @__PURE__ */ jsx2(L, { children: b.items.map((item, j) => /* @__PURE__ */ jsx2("li", { children: renderInline(item) }, j)) }, i);
    }
    case "quote":
      return /* @__PURE__ */ jsx2("blockquote", { children: b.children.map(renderBlock) }, i);
    case "codeblock":
      return /* @__PURE__ */ jsx2("pre", { children: /* @__PURE__ */ jsx2("code", { children: b.text }) }, i);
    case "rule":
      return /* @__PURE__ */ jsx2("hr", {}, i);
  }
}
function Markdown({ source, images = false }) {
  const blocks = useMemo(() => parseMarkdown(source, { images }), [source, images]);
  return /* @__PURE__ */ jsx2("div", { className: "lc-md", children: blocks.map(renderBlock) });
}

// src/web/screens/shared.tsx
import { Component, createContext, useContext } from "react";

// src/web/util.ts
import { onColor } from "@kobecuppens/livechat-core";
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase() || "?";
}
var rtfCache = /* @__PURE__ */ new Map();
var dateCache = /* @__PURE__ */ new Map();
var clockCache = /* @__PURE__ */ new Map();
function cached(cache, locale, make) {
  let f = cache.get(locale);
  if (!f) {
    f = make();
    cache.set(locale, f);
  }
  return f;
}
function relativeTime(ts, locale, now = Date.now()) {
  const diff = (ts - now) / 1e3;
  const rtf = cached(rtfCache, locale, () => new Intl.RelativeTimeFormat(locale, { numeric: "auto", style: "short" }));
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(0, "second");
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
  if (abs < 7 * 86400) return rtf.format(Math.round(diff / 86400), "day");
  return cached(dateCache, locale, () => new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" })).format(ts);
}
function clockTime(ts, locale) {
  return cached(clockCache, locale, () => new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" })).format(ts);
}

// src/web/screens/shared.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var InlineContext = createContext(false);
function Header({
  title,
  subtitle,
  avatar,
  focusTarget = true
}) {
  const messenger = useMessenger();
  const t = useTranslate();
  return /* @__PURE__ */ jsxs2("div", { className: "lc-header", children: [
    messenger.canGoBack && /* @__PURE__ */ jsx3("button", { type: "button", className: "lc-icon-btn", onClick: messenger.back, "aria-label": t("common.back"), children: /* @__PURE__ */ jsx3(BackIcon, {}) }),
    avatar,
    /* @__PURE__ */ jsxs2("div", { className: "lc-header-title", children: [
      /* @__PURE__ */ jsx3("strong", { "data-screen-title": focusTarget || void 0, tabIndex: focusTarget ? -1 : void 0, children: title }),
      subtitle && /* @__PURE__ */ jsx3("span", { children: subtitle })
    ] }),
    /* @__PURE__ */ jsx3(CloseButton, {})
  ] });
}
function CloseButton() {
  const messenger = useMessenger();
  const t = useTranslate();
  if (useContext(InlineContext)) return null;
  return /* @__PURE__ */ jsx3("button", { type: "button", className: "lc-icon-btn", onClick: messenger.close, "aria-label": t("common.close"), children: /* @__PURE__ */ jsx3(CloseIcon, {}) });
}
function Avatar({ name, url, brand }) {
  return /* @__PURE__ */ jsx3("span", { className: `lc-avatar${brand ? " lc-brand" : ""}`, children: url ? /* @__PURE__ */ jsx3("img", { src: url, alt: "" }) : initials(name) });
}
function Loading() {
  const t = useTranslate();
  return /* @__PURE__ */ jsx3("div", { className: "lc-loading", role: "status", "aria-label": t("common.loading"), children: /* @__PURE__ */ jsx3("div", { className: "lc-spinner" }) });
}
function ErrorState({ onRetry }) {
  const t = useTranslate();
  return /* @__PURE__ */ jsxs2("div", { className: "lc-error-state", role: "alert", children: [
    /* @__PURE__ */ jsx3("div", { children: t("common.error") }),
    onRetry && /* @__PURE__ */ jsx3("button", { type: "button", onClick: onRetry, children: t("common.retry") })
  ] });
}
var MessengerErrorBoundary = class extends Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error, info) {
    console.error("[livechat] render error", error, info.componentStack);
  }
  render() {
    return this.state.failed ? this.props.fallback(() => this.setState({ failed: false })) : this.props.children;
  }
};

// src/web/screens/Articles.tsx
import { Fragment as Fragment2, jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
function ArticleRows({ articles }) {
  const messenger = useMessenger();
  return /* @__PURE__ */ jsx4(Fragment2, { children: articles.map((a) => /* @__PURE__ */ jsxs3("button", { type: "button", className: "lc-row", onClick: () => messenger.navigate({ name: "article", slug: a.slug }), children: [
    /* @__PURE__ */ jsxs3("div", { className: "lc-row-main", children: [
      /* @__PURE__ */ jsx4("strong", { children: a.title }),
      a.excerpt && /* @__PURE__ */ jsx4("span", { children: a.excerpt })
    ] }),
    /* @__PURE__ */ jsx4(ChevronIcon, {})
  ] }, a.id)) });
}
function CategoryScreen({ id, title }) {
  const { data, loading, error, reload } = useCategoryArticles(id);
  return /* @__PURE__ */ jsxs3(Fragment2, { children: [
    /* @__PURE__ */ jsx4(Header, { title }),
    /* @__PURE__ */ jsx4("div", { className: "lc-body", children: loading && !data ? /* @__PURE__ */ jsx4(Loading, {}) : error ? /* @__PURE__ */ jsx4(ErrorState, { onRetry: reload }) : /* @__PURE__ */ jsx4(ArticleRows, { articles: data ?? [] }) })
  ] });
}
function ArticleScreen({ slug }) {
  const t = useTranslate();
  const messenger = useMessenger();
  const { data, loading, error, reload, feedback, sendFeedback } = useArticle(slug);
  return /* @__PURE__ */ jsxs3(Fragment2, { children: [
    /* @__PURE__ */ jsx4(Header, { title: data?.title ?? "" }),
    /* @__PURE__ */ jsx4("div", { className: "lc-body", children: loading && !data ? /* @__PURE__ */ jsx4(Loading, {}) : error || !data ? /* @__PURE__ */ jsx4(ErrorState, { onRetry: reload }) : /* @__PURE__ */ jsxs3(Fragment2, { children: [
      /* @__PURE__ */ jsxs3("article", { className: "lc-article", children: [
        /* @__PURE__ */ jsx4("h1", { children: data.title }),
        /* @__PURE__ */ jsx4(Markdown, { source: data.bodyMd, images: true })
      ] }),
      /* @__PURE__ */ jsx4("div", { className: "lc-feedback", children: feedback === null ? /* @__PURE__ */ jsxs3(Fragment2, { children: [
        /* @__PURE__ */ jsx4("div", { children: t("faq.helpful") }),
        /* @__PURE__ */ jsxs3("div", { className: "lc-feedback-btns", children: [
          /* @__PURE__ */ jsxs3("button", { type: "button", className: "lc-pill", onClick: () => sendFeedback(true), children: [
            "\u{1F44D} ",
            t("faq.yes")
          ] }),
          /* @__PURE__ */ jsxs3("button", { type: "button", className: "lc-pill", onClick: () => sendFeedback(false), children: [
            "\u{1F44E} ",
            t("faq.no")
          ] })
        ] })
      ] }) : /* @__PURE__ */ jsx4("div", { children: t("faq.thanks") }) }),
      /* @__PURE__ */ jsxs3("div", { className: "lc-still", children: [
        /* @__PURE__ */ jsx4("span", { children: t("faq.stillNeedHelp") }),
        /* @__PURE__ */ jsxs3("button", { type: "button", className: "lc-btn", onClick: () => messenger.navigate({ name: "new" }), children: [
          /* @__PURE__ */ jsx4(ChatIcon, {}),
          t("home.startChat")
        ] })
      ] })
    ] }) })
  ] });
}

// src/web/screens/Chat.tsx
import {
  ALLOWED_ATTACHMENT_TYPES,
  LiveChatApiError,
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS_PER_MESSAGE
} from "@kobecuppens/livechat-core";
import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Fragment as Fragment3, jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
function imageSize(file) {
  if (!file.type.startsWith("image/") || typeof Image === "undefined") return Promise.resolve({});
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve({});
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}
function Attachments({ items }) {
  if (items.length === 0) return null;
  return /* @__PURE__ */ jsx5("div", { className: "lc-attachments", children: items.map(
    (a) => a.contentType.startsWith("image/") && a.url ? /* @__PURE__ */ jsx5("a", { href: a.url, target: "_blank", rel: "noopener noreferrer", children: /* @__PURE__ */ jsx5("img", { className: "lc-attachment-img", src: a.url, alt: a.name, width: a.width, height: a.height, loading: "lazy" }) }, a.id) : /* @__PURE__ */ jsxs4("a", { className: "lc-attachment-file", href: a.url, target: "_blank", rel: "noopener noreferrer", children: [
      /* @__PURE__ */ jsx5(FileIcon, {}),
      a.name
    ] }, a.id)
  ) });
}
var CSAT_FACES = ["\u{1F61E}", "\u{1F641}", "\u{1F610}", "\u{1F642}", "\u{1F60D}"];
function CsatCard({ score, onSubmit }) {
  const t = useTranslate();
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  if (score !== null) return /* @__PURE__ */ jsx5("div", { className: "lc-system", children: t("csat.thanks") });
  return /* @__PURE__ */ jsxs4("div", { className: "lc-csat", children: [
    /* @__PURE__ */ jsx5("strong", { children: t("csat.question") }),
    /* @__PURE__ */ jsx5(
      "div",
      {
        className: "lc-csat-scores",
        role: "radiogroup",
        "aria-label": t("csat.question"),
        onKeyDown: (e) => {
          const step = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
          if (!step) return;
          e.preventDefault();
          const n = CSAT_FACES.length;
          const current = selected ?? (step > 0 ? n : 1);
          const next = (current - 1 + step + n) % n + 1;
          setSelected(next);
          e.currentTarget.children[next - 1]?.focus();
        },
        children: CSAT_FACES.map((face, i) => (
          // biome-ignore lint/a11y/useSemanticElements: ARIA radio pattern on buttons (emoji faces can't be styled as native radios)
          /* @__PURE__ */ jsx5(
            "button",
            {
              type: "button",
              role: "radio",
              tabIndex: (selected ?? 1) === i + 1 ? 0 : -1,
              "aria-checked": selected === i + 1,
              "aria-label": t("csat.score", { score: i + 1 }),
              onClick: () => setSelected(i + 1),
              children: face
            },
            face
          )
        ))
      }
    ),
    selected !== null && /* @__PURE__ */ jsxs4(Fragment3, { children: [
      /* @__PURE__ */ jsx5("textarea", { value: comment, onChange: (e) => setComment(e.target.value), placeholder: t("csat.commentPlaceholder"), maxLength: 2e3 }),
      /* @__PURE__ */ jsx5(
        "button",
        {
          type: "button",
          className: "lc-btn",
          disabled: busy,
          onClick: async () => {
            setBusy(true);
            setFailed(false);
            try {
              await onSubmit(selected, comment.trim() || void 0);
            } catch {
              setFailed(true);
            } finally {
              setBusy(false);
            }
          },
          children: busy ? t("chat.sending") : t("csat.submit")
        }
      ),
      failed && /* @__PURE__ */ jsx5("div", { className: "lc-meta lc-danger", role: "alert", children: t("common.error") })
    ] })
  ] });
}
function SystemMessage({ message }) {
  const t = useTranslate();
  switch (message.systemEvent) {
    case "resolved":
      return /* @__PURE__ */ jsx5("div", { className: "lc-system", children: t("chat.resolved") });
    case "reopened":
      return /* @__PURE__ */ jsx5("div", { className: "lc-system", children: t("chat.reopened") });
    case "assigned":
      return /* @__PURE__ */ jsx5("div", { className: "lc-system", children: t("chat.assigned", { name: message.body }) });
    case "auto_reply":
      return /* @__PURE__ */ jsx5("div", { className: "lc-system lc-auto", children: message.body });
    default:
      return null;
  }
}
var MessageList = memo(function MessageList2({
  messages,
  csatScore,
  agentLastReadAt,
  locale,
  onRetry,
  onCsat
}) {
  let lastMine;
  for (let i = messages.length - 1; i >= 0 && !lastMine; i--) {
    const m = messages[i];
    if (m.authorType === "contact" && m.status === "sent") lastMine = m;
  }
  const seen = lastMine && agentLastReadAt >= lastMine.createdAt;
  return /* @__PURE__ */ jsx5(Fragment3, { children: messages.map((m, i) => {
    if (m.authorType === "system") {
      if (m.systemEvent === "csat_request") {
        return /* @__PURE__ */ jsx5(CsatCard, { score: csatScore, onSubmit: onCsat }, m.id);
      }
      return /* @__PURE__ */ jsx5(SystemMessage, { message: m }, m.id);
    }
    const prev = messages[i - 1];
    const next = messages[i + 1];
    return /* @__PURE__ */ jsx5(
      MessageRow,
      {
        m,
        first: !prev || prev.authorType !== m.authorType || prev.author?.id !== m.author?.id,
        lastOfGroup: !next || next.authorType !== m.authorType || next.author?.id !== m.author?.id,
        seen: m === lastMine && !!seen,
        locale,
        onRetry
      },
      m.id
    );
  }) });
});
var MessageRow = memo(function MessageRow2({
  m,
  first,
  lastOfGroup,
  seen,
  locale,
  onRetry
}) {
  const t = useTranslate();
  const mine = m.authorType === "contact";
  return /* @__PURE__ */ jsxs4("div", { className: `lc-msg${mine ? " lc-mine" : ""}${first ? " lc-first" : ""}`, children: [
    !mine && /* @__PURE__ */ jsx5("span", { className: lastOfGroup ? "" : "lc-avatar lc-hidden", children: lastOfGroup && /* @__PURE__ */ jsx5(Avatar, { name: m.author?.name, url: m.author?.avatarUrl }) }),
    /* @__PURE__ */ jsxs4("div", { className: "lc-bubble-wrap", children: [
      !mine && first && /* @__PURE__ */ jsx5("div", { className: "lc-author", children: m.author?.name ?? t("chat.support") }),
      m.body && /* @__PURE__ */ jsx5(
        "div",
        {
          className: `lc-bubble${m.status === "sending" ? " lc-pending" : ""}${m.status === "failed" ? " lc-failed" : ""}`,
          title: clockTime(m.createdAt, locale),
          children: /* @__PURE__ */ jsx5(Markdown, { source: m.body })
        }
      ),
      /* @__PURE__ */ jsx5(Attachments, { items: m.attachments }),
      m.status === "failed" && /* @__PURE__ */ jsx5("button", { type: "button", className: "lc-meta lc-danger", onClick: () => void onRetry(m.clientId), children: t("chat.failed") }),
      m.status === "sending" && /* @__PURE__ */ jsx5("div", { className: "lc-meta", children: t("chat.sending") }),
      seen && /* @__PURE__ */ jsx5("div", { className: "lc-meta", children: t("chat.seen") })
    ] })
  ] });
});
function ChatScreen({ conversationId }) {
  const t = useTranslate();
  const client = useLiveChatClient();
  const messenger = useMessenger();
  const config = useWorkspaceConfig();
  const locale = useLiveChatState((s) => s.locale);
  const onCreated = useCallback((id) => messenger.replace({ name: "conversation", id }), [messenger]);
  const { thread, conversation, send, retry, loadOlder, setTyping, submitCsat } = useConversation(conversationId, onCreated);
  const [draft, setDraft] = useState("");
  const [uploads, setUploads] = useState([]);
  const fileInput = useRef(null);
  const textarea = useRef(null);
  const scroller = useRef(null);
  const stickToBottom = useRef(true);
  const suggestions = useArticleSuggestions(conversationId ? "" : draft);
  const messages = thread.messages;
  const prevEnds = useRef({ first: void 0, last: void 0, height: 0 });
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const first = messages[0]?.id;
    const lastMsg = messages[messages.length - 1];
    const p = prevEnds.current;
    if (p.first && first !== p.first && lastMsg?.id === p.last) {
      el.scrollTop += el.scrollHeight - p.height;
    } else if (stickToBottom.current || lastMsg?.id !== p.last && lastMsg?.authorType === "contact") {
      el.scrollTop = el.scrollHeight;
    }
    prevEnds.current = { first, last: lastMsg?.id, height: el.scrollHeight };
  }, [messages, thread.typing]);
  useEffect(() => {
    textarea.current?.focus();
  }, [conversationId]);
  const uploading = uploads.some((u) => u.state === "uploading");
  const ready = uploads.filter((u) => u.state === "done");
  const canSend = !uploading && (draft.trim().length > 0 || ready.length > 0);
  const submit = () => {
    if (!canSend) return;
    const attachments = ready.map((u) => u.attachment);
    void send(draft.trim(), attachments.map((a) => a.id), attachments);
    setDraft("");
    setUploads([]);
    stickToBottom.current = true;
  };
  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };
  const addFiles = async (files) => {
    if (!files) return;
    const room = MAX_ATTACHMENTS_PER_MESSAGE - uploads.length;
    for (const file of [...files].slice(0, Math.max(0, room))) {
      const key = `${file.name}-${file.size}-${Math.random()}`;
      const fail = (error) => setUploads((u) => u.map((x) => x.key === key ? { ...x, state: "error", error } : x));
      setUploads((u) => [...u, { key, name: file.name, state: "uploading" }]);
      if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
        fail(t("chat.attachmentType"));
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        fail(t("chat.attachmentTooLarge", { mb: MAX_ATTACHMENT_BYTES / 1024 / 1024 }));
        continue;
      }
      try {
        const attachment = await client.uploadAttachment({ body: file, name: file.name, type: file.type, size: file.size, ...await imageSize(file) });
        setUploads((u) => u.map((x) => x.key === key ? { ...x, state: "done", attachment } : x));
      } catch (err) {
        fail(err instanceof LiveChatApiError && err.code === "too_large" ? t("chat.attachmentTooLarge", { mb: MAX_ATTACHMENT_BYTES / 1024 / 1024 }) : t("common.error"));
      }
    }
  };
  const agentName = conversation?.assignee?.name ?? config?.branding.name ?? t("chat.support");
  return /* @__PURE__ */ jsxs4(Fragment3, { children: [
    /* @__PURE__ */ jsx5(
      Header,
      {
        title: conversationId ? agentName : t("chat.newConversation"),
        subtitle: config?.online ? config.typicalReplyMinutes ? t("status.replyTime", { minutes: config.typicalReplyMinutes }) : t("status.online") : t("status.offline"),
        avatar: /* @__PURE__ */ jsx5(Avatar, { name: agentName, url: conversation?.assignee?.avatarUrl, brand: !conversation?.assignee })
      }
    ),
    conversationId && thread.loaded && thread.connection === "connecting" && /* @__PURE__ */ jsx5("div", { className: "lc-banner", children: t("common.offline") }),
    /* @__PURE__ */ jsx5(
      "div",
      {
        className: "lc-body",
        ref: scroller,
        onScroll: (e) => {
          const el = e.currentTarget;
          stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        },
        children: conversationId && !thread.loaded && thread.error ? /* @__PURE__ */ jsx5(ErrorState, { onRetry: () => void client.retryLoad(conversationId) }) : conversationId && !thread.loaded ? /* @__PURE__ */ jsx5(Loading, {}) : /* @__PURE__ */ jsxs4("div", { className: "lc-thread", role: "log", "aria-live": "polite", children: [
          thread.hasOlder && /* @__PURE__ */ jsx5("button", { type: "button", className: "lc-older", onClick: () => void loadOlder(), disabled: thread.loading, children: t("chat.loadOlder") }),
          /* @__PURE__ */ jsx5(
            MessageList,
            {
              messages,
              csatScore: conversation?.csatScore ?? null,
              agentLastReadAt: thread.agentLastReadAt,
              locale,
              onRetry: retry,
              onCsat: submitCsat
            }
          ),
          thread.typing && /* @__PURE__ */ jsxs4("div", { className: "lc-msg lc-first", children: [
            /* @__PURE__ */ jsx5("span", { className: "lc-sr", children: thread.typing.name ? t("chat.typing", { name: thread.typing.name }) : t("chat.someoneTyping") }),
            /* @__PURE__ */ jsx5(Avatar, { name: thread.typing.name ?? agentName }),
            /* @__PURE__ */ jsxs4("div", { className: "lc-typing", "aria-hidden": "true", children: [
              /* @__PURE__ */ jsx5("i", {}),
              /* @__PURE__ */ jsx5("i", {}),
              /* @__PURE__ */ jsx5("i", {})
            ] })
          ] })
        ] })
      }
    ),
    !conversationId && suggestions.length > 0 && /* @__PURE__ */ jsxs4("div", { className: "lc-suggest", children: [
      /* @__PURE__ */ jsx5("div", { className: "lc-card-title", children: t("chat.suggestedArticles") }),
      suggestions.map((a) => /* @__PURE__ */ jsx5("button", { type: "button", className: "lc-row", onClick: () => messenger.navigate({ name: "article", slug: a.slug }), children: /* @__PURE__ */ jsx5("div", { className: "lc-row-main", children: /* @__PURE__ */ jsx5("strong", { children: a.title }) }) }, a.id))
    ] }),
    /* @__PURE__ */ jsxs4("div", { className: "lc-composer", children: [
      uploads.length > 0 && /* @__PURE__ */ jsx5("div", { className: "lc-chips", "aria-live": "polite", children: uploads.map((u) => /* @__PURE__ */ jsxs4("div", { className: `lc-chip${u.state === "uploading" ? " lc-uploading" : ""}${u.state === "error" ? " lc-chip-error" : ""}`, title: u.error, children: [
        /* @__PURE__ */ jsx5("span", { children: u.state === "error" ? `${u.name} \u2014 ${u.error}` : u.name }),
        /* @__PURE__ */ jsx5("button", { type: "button", "aria-label": t("chat.removeAttachment", { name: u.name }), onClick: () => setUploads((all) => all.filter((x) => x.key !== u.key)), children: /* @__PURE__ */ jsx5(CloseIcon, {}) })
      ] }, u.key)) }),
      /* @__PURE__ */ jsxs4("div", { className: "lc-composer-row", children: [
        /* @__PURE__ */ jsx5("button", { type: "button", className: "lc-icon-btn", onClick: () => fileInput.current?.click(), "aria-label": t("chat.attach"), disabled: uploads.length >= MAX_ATTACHMENTS_PER_MESSAGE, children: /* @__PURE__ */ jsx5(PaperclipIcon, {}) }),
        /* @__PURE__ */ jsx5(
          "input",
          {
            ref: fileInput,
            type: "file",
            hidden: true,
            multiple: true,
            accept: ALLOWED_ATTACHMENT_TYPES.join(","),
            onChange: (e) => {
              void addFiles(e.target.files);
              e.target.value = "";
            }
          }
        ),
        /* @__PURE__ */ jsx5(
          "textarea",
          {
            ref: textarea,
            rows: 1,
            value: draft,
            placeholder: t("chat.placeholder"),
            "aria-label": t("chat.placeholder"),
            onKeyDown,
            onChange: (e) => {
              setDraft(e.target.value);
              setTyping(e.target.value.length > 0);
              e.target.style.height = "auto";
              e.target.style.height = `${Math.min(e.target.scrollHeight, 140)}px`;
            },
            onPaste: (e) => {
              if (e.clipboardData.files.length > 0) {
                e.preventDefault();
                void addFiles(e.clipboardData.files);
              }
            }
          }
        ),
        /* @__PURE__ */ jsx5("button", { type: "button", className: "lc-send", onClick: submit, disabled: !canSend, "aria-label": t("chat.send"), children: /* @__PURE__ */ jsx5(SendIcon, {}) })
      ] })
    ] })
  ] });
}

// src/web/screens/Conversations.tsx
import { Fragment as Fragment4, jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
function ConversationsScreen() {
  const t = useTranslate();
  const messenger = useMessenger();
  const config = useWorkspaceConfig();
  const locale = useLiveChatState((s) => s.locale);
  const { conversations, loaded, error, refresh } = useConversations();
  return /* @__PURE__ */ jsxs5(Fragment4, { children: [
    /* @__PURE__ */ jsx6(Header, { title: t("home.recentConversations") }),
    /* @__PURE__ */ jsx6("div", { className: "lc-body", children: !loaded ? error ? /* @__PURE__ */ jsx6(ErrorState, { onRetry: refresh }) : /* @__PURE__ */ jsx6(Loading, {}) : conversations.length === 0 ? /* @__PURE__ */ jsx6("div", { className: "lc-empty", children: t("chat.empty") }) : conversations.map((c) => /* @__PURE__ */ jsxs5("button", { type: "button", className: "lc-row", onClick: () => messenger.navigate({ name: "conversation", id: c.id }), children: [
      /* @__PURE__ */ jsx6(Avatar, { name: c.assignee?.name ?? config?.branding.name, url: c.assignee?.avatarUrl, brand: !c.assignee }),
      /* @__PURE__ */ jsxs5("div", { className: "lc-row-main", children: [
        /* @__PURE__ */ jsx6("strong", { children: c.assignee?.name ?? config?.branding.name }),
        /* @__PURE__ */ jsx6("span", { children: c.lastMessage?.body || "\u{1F4CE}" })
      ] }),
      c.unreadCount > 0 && /* @__PURE__ */ jsxs5(Fragment4, { children: [
        /* @__PURE__ */ jsx6("i", { className: "lc-dot", "aria-hidden": "true" }),
        /* @__PURE__ */ jsx6("span", { className: "lc-sr", children: t("chat.unread") })
      ] }),
      /* @__PURE__ */ jsx6("span", { className: "lc-row-meta", children: relativeTime(c.lastMessageAt, locale) })
    ] }, c.id)) })
  ] });
}

// src/web/screens/Home.tsx
import { useState as useState2 } from "react";
import { Fragment as Fragment5, jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
function HomeScreen() {
  const t = useTranslate();
  const messenger = useMessenger();
  const config = useWorkspaceConfig();
  const locale = useLiveChatState((s) => s.locale);
  const [query, setQuery] = useState2("");
  const home = useHelpHome();
  const search = useHelpSearch(query);
  const { conversations } = useConversations();
  const recent = conversations.slice(0, 3);
  const greeting = config?.branding.greeting[locale] ?? t("home.greeting");
  const searching = query.trim().length >= 2;
  return /* @__PURE__ */ jsxs6("div", { className: "lc-body", children: [
    /* @__PURE__ */ jsxs6("div", { className: "lc-hero", children: [
      /* @__PURE__ */ jsxs6("div", { className: "lc-hero-top", children: [
        config?.branding.logoUrl ? /* @__PURE__ */ jsx7("img", { src: config.branding.logoUrl, alt: config.branding.name }) : /* @__PURE__ */ jsx7("strong", { children: config?.branding.name }),
        /* @__PURE__ */ jsx7(CloseButton, {})
      ] }),
      /* @__PURE__ */ jsx7("h1", { "data-screen-title": true, tabIndex: -1, children: greeting })
    ] }),
    /* @__PURE__ */ jsxs6("div", { className: "lc-home-cards", children: [
      /* @__PURE__ */ jsxs6("div", { className: "lc-card", children: [
        /* @__PURE__ */ jsxs6("label", { className: "lc-search", children: [
          /* @__PURE__ */ jsx7(SearchIcon, {}),
          /* @__PURE__ */ jsx7("span", { className: "lc-sr", children: t("home.searchPlaceholder") }),
          /* @__PURE__ */ jsx7(
            "input",
            {
              type: "search",
              value: query,
              onChange: (e) => setQuery(e.target.value),
              placeholder: t("home.searchPlaceholder"),
              autoComplete: "off"
            }
          )
        ] }),
        searching && (search.error && !search.pending ? (
          // A failed search is not "no results": say so and offer a retry.
          /* @__PURE__ */ jsx7(ErrorState, { onRetry: search.reload })
        ) : search.pending && !search.data ? /* @__PURE__ */ jsx7(Loading, {}) : search.data && search.data.length > 0 ? /* @__PURE__ */ jsx7(ArticleRows, { articles: search.data }) : /* @__PURE__ */ jsx7("div", { className: "lc-empty", children: t("faq.noResults", { query: query.trim() }) }))
      ] }),
      !searching && /* @__PURE__ */ jsxs6(Fragment5, { children: [
        /* @__PURE__ */ jsx7("div", { className: "lc-card", children: /* @__PURE__ */ jsxs6("button", { type: "button", className: "lc-cta", onClick: () => messenger.navigate({ name: "new" }), children: [
          /* @__PURE__ */ jsxs6("div", { className: "lc-cta-main", children: [
            /* @__PURE__ */ jsx7("strong", { children: t("home.startChat") }),
            /* @__PURE__ */ jsxs6("span", { children: [
              /* @__PURE__ */ jsx7("i", { className: `lc-status-dot${config?.online ? " lc-online" : ""}` }),
              config?.online ? config.typicalReplyMinutes ? t("status.replyTime", { minutes: config.typicalReplyMinutes }) : t("status.online") : t("status.offline")
            ] })
          ] }),
          /* @__PURE__ */ jsx7(SendIcon, {})
        ] }) }),
        recent.length > 0 && /* @__PURE__ */ jsxs6("div", { className: "lc-card", children: [
          /* @__PURE__ */ jsx7("div", { className: "lc-card-title", children: t("home.recentConversations") }),
          recent.map((c) => /* @__PURE__ */ jsxs6("button", { type: "button", className: "lc-row", onClick: () => messenger.navigate({ name: "conversation", id: c.id }), children: [
            /* @__PURE__ */ jsxs6("div", { className: "lc-row-main", children: [
              /* @__PURE__ */ jsx7("strong", { children: c.assignee?.name ?? config?.branding.name }),
              /* @__PURE__ */ jsx7("span", { children: c.lastMessage?.body || "\u{1F4CE}" })
            ] }),
            c.unreadCount > 0 && /* @__PURE__ */ jsxs6(Fragment5, { children: [
              /* @__PURE__ */ jsx7("i", { className: "lc-dot", "aria-hidden": "true" }),
              /* @__PURE__ */ jsx7("span", { className: "lc-sr", children: t("chat.unread") })
            ] }),
            /* @__PURE__ */ jsx7("span", { className: "lc-row-meta", children: relativeTime(c.lastMessageAt, locale) })
          ] }, c.id)),
          conversations.length > 3 && /* @__PURE__ */ jsxs6("button", { type: "button", className: "lc-row", onClick: () => messenger.navigate({ name: "conversations" }), children: [
            /* @__PURE__ */ jsx7("div", { className: "lc-row-main", children: /* @__PURE__ */ jsx7("strong", { children: t("home.recentConversations") }) }),
            /* @__PURE__ */ jsx7(ChevronIcon, {})
          ] })
        ] }),
        home.error && !home.data ? /* @__PURE__ */ jsx7(ErrorState, { onRetry: home.reload }) : home.loading && !home.data ? /* @__PURE__ */ jsx7(Loading, {}) : /* @__PURE__ */ jsxs6(Fragment5, { children: [
          home.data && home.data.popular.length > 0 && /* @__PURE__ */ jsxs6("div", { className: "lc-card", children: [
            /* @__PURE__ */ jsx7("div", { className: "lc-card-title", children: t("home.popularArticles") }),
            /* @__PURE__ */ jsx7(ArticleRows, { articles: home.data.popular })
          ] }),
          home.data && home.data.categories.length > 0 && /* @__PURE__ */ jsxs6("div", { className: "lc-card", children: [
            /* @__PURE__ */ jsx7("div", { className: "lc-card-title", children: t("home.categories") }),
            home.data.categories.map((c) => /* @__PURE__ */ jsxs6("button", { type: "button", className: "lc-row", onClick: () => messenger.navigate({ name: "category", id: c.id, title: c.title }), children: [
              /* @__PURE__ */ jsx7(ArticleIcon, {}),
              /* @__PURE__ */ jsxs6("div", { className: "lc-row-main", children: [
                /* @__PURE__ */ jsx7("strong", { children: c.title }),
                /* @__PURE__ */ jsx7("span", { children: c.description ?? t("faq.articlesCount", { count: c.articleCount }) })
              ] }),
              /* @__PURE__ */ jsx7(ChevronIcon, {})
            ] }, c.id))
          ] })
        ] })
      ] })
    ] })
  ] });
}

// src/web/styles.ts
var MESSENGER_CSS = `
.lc-root{
  --lc-primary:#4F46E5;--lc-on-primary:#fff;
  --lc-bg:#fff;--lc-surface:#f6f7f9;--lc-surface-2:#eceef2;--lc-border:#e3e5ea;
  --lc-text:#111827;--lc-muted:#6b7280;--lc-danger:#dc2626;--lc-success:#16a34a;
  --lc-radius:16px;--lc-shadow:0 12px 48px rgba(17,24,39,.18),0 2px 8px rgba(17,24,39,.08);
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;
  font-size:15px;line-height:1.45;color:var(--lc-text);-webkit-font-smoothing:antialiased;
}
@media (prefers-color-scheme:dark){.lc-root:not([data-theme="light"]){
  --lc-bg:#15171c;--lc-surface:#1e2128;--lc-surface-2:#272b33;--lc-border:#2e323b;--lc-text:#f3f4f6;--lc-muted:#9ca3af;--lc-danger:#f87171;
  --lc-shadow:0 12px 48px rgba(0,0,0,.5);
}}
.lc-root[data-theme="dark"]{--lc-bg:#15171c;--lc-surface:#1e2128;--lc-surface-2:#272b33;--lc-border:#2e323b;--lc-text:#f3f4f6;--lc-muted:#9ca3af;--lc-danger:#f87171}
.lc-root *,.lc-root *::before,.lc-root *::after{box-sizing:border-box}
/* :where keeps the reset at class specificity, so component rules below (e.g. .lc-cta padding) win by order. */
.lc-root :where(button){font:inherit;color:inherit;background:none;border:0;padding:0;cursor:pointer}
.lc-root button:focus-visible,.lc-root a:focus-visible,.lc-root textarea:focus-visible,.lc-root input:focus-visible{outline:2px solid var(--lc-primary);outline-offset:2px}
.lc-sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}

.lc-launcher{position:fixed;right:20px;bottom:20px;z-index:2147483000;width:60px;height:60px;border-radius:50%;
  background:var(--lc-primary)!important;color:var(--lc-on-primary)!important;box-shadow:var(--lc-shadow);display:grid;place-items:center;transition:transform .15s}
.lc-launcher:hover{transform:scale(1.05)}
.lc-launcher svg{width:28px;height:28px}
.lc-badge{position:absolute;top:-2px;right:-2px;min-width:20px;height:20px;padding:0 6px;border-radius:10px;background:#dc2626;
  color:#fff;font-size:12px;font-weight:600;display:grid;place-items:center;border:2px solid var(--lc-bg)}

.lc-panel{position:fixed;right:20px;bottom:92px;z-index:2147483000;width:400px;height:min(680px,calc(100vh - 120px));
  background:var(--lc-bg);border-radius:var(--lc-radius);box-shadow:var(--lc-shadow);display:flex;flex-direction:column;overflow:hidden;
  animation:lc-in .18s ease-out}
.lc-panel.lc-inline{position:relative;right:auto;bottom:auto;width:100%;height:100%;box-shadow:none;border:1px solid var(--lc-border);animation:none}
/* Full screen on phones; the launcher would cover the composer's send button, so it hides (the header has Close). */
@media (max-width:480px){.lc-panel:not(.lc-inline){inset:0;width:auto;height:auto;border-radius:0}.lc-launcher.lc-open{display:none}}
@keyframes lc-in{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}

.lc-header{display:flex;align-items:center;gap:8px;padding:12px 12px;border-bottom:1px solid var(--lc-border);min-height:56px}
.lc-header-title{flex:1;min-width:0}
.lc-header-title strong{display:block;font-size:15px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.lc-header-title span{display:block;font-size:12px;color:var(--lc-muted)}
.lc-icon-btn{width:36px;height:36px;border-radius:10px;display:grid;place-items:center;color:var(--lc-muted)}
.lc-icon-btn:hover{background:var(--lc-surface)}
.lc-icon-btn svg{width:20px;height:20px}
.lc-body{flex:1;overflow-y:auto;overscroll-behavior:contain}

.lc-hero{background:var(--lc-primary);color:var(--lc-on-primary);padding:24px 20px 56px;position:relative}
.lc-hero-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:20px}
.lc-hero img{height:28px;max-width:140px;object-fit:contain}
.lc-hero h1{font-size:24px;line-height:1.25;margin:0;font-weight:700}
.lc-hero .lc-icon-btn{color:inherit}
.lc-hero .lc-icon-btn:hover{background:rgba(255,255,255,.15)}
.lc-home-cards{margin-top:-40px;padding:0 16px 16px;display:flex;flex-direction:column;gap:12px;position:relative;z-index:1}
.lc-card{background:var(--lc-bg);border:1px solid var(--lc-border);border-radius:14px;box-shadow:0 2px 8px rgba(17,24,39,.05);overflow:hidden}
.lc-card-title{font-size:13px;font-weight:600;color:var(--lc-muted);padding:14px 16px 6px;text-transform:uppercase;letter-spacing:.02em}

.lc-search{display:flex;align-items:center;gap:8px;padding:0 14px;height:48px}
.lc-search svg{width:18px;height:18px;color:var(--lc-muted);flex:none}
.lc-search input{flex:1;border:0;background:none;font:inherit;color:inherit;height:100%;outline:none}
.lc-search input::placeholder{color:var(--lc-muted)}

.lc-row{display:flex;align-items:center;gap:12px;width:100%;padding:12px 16px;text-align:left;border-top:1px solid var(--lc-border)}
.lc-card .lc-row:first-child,.lc-card-title+.lc-row{border-top:0}
.lc-row:hover{background:var(--lc-surface)}
.lc-row-main{flex:1;min-width:0}
.lc-row-main strong{display:block;font-weight:500}
.lc-row-main span{display:block;font-size:13px;color:var(--lc-muted);overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical}
.lc-row>svg{width:18px;height:18px;color:var(--lc-muted);flex:none}
.lc-row-meta{font-size:12px;color:var(--lc-muted);white-space:nowrap}
.lc-dot{width:8px;height:8px;border-radius:50%;background:var(--lc-danger);flex:none}

.lc-cta{display:flex;align-items:center;gap:12px;padding:16px;width:100%;text-align:left}
.lc-cta:hover{background:var(--lc-surface)}
.lc-cta-main{flex:1}
.lc-cta-main strong{display:block}
.lc-cta-main span{display:flex;align-items:center;gap:6px;font-size:13px;color:var(--lc-muted)}
.lc-cta>svg{width:20px;height:20px;color:var(--lc-primary)}
.lc-status-dot{width:8px;height:8px;border-radius:50%;background:var(--lc-muted)}
.lc-status-dot.lc-online{background:var(--lc-success)}

.lc-avatar{width:32px;height:32px;border-radius:50%;background:var(--lc-surface-2);color:var(--lc-text);display:grid;place-items:center;
  font-size:13px;font-weight:600;flex:none;overflow:hidden}
.lc-avatar img{width:100%;height:100%;object-fit:cover}
.lc-avatar.lc-brand{background:var(--lc-primary);color:var(--lc-on-primary)}

.lc-empty,.lc-loading,.lc-error-state{padding:32px 20px;text-align:center;color:var(--lc-muted)}
.lc-error-state button{margin-top:8px;color:var(--lc-primary);font-weight:500}
.lc-spinner{width:22px;height:22px;border:2px solid var(--lc-border);border-top-color:var(--lc-primary);border-radius:50%;animation:lc-spin .8s linear infinite;margin:0 auto}
@keyframes lc-spin{to{transform:rotate(360deg)}}

.lc-article{padding:20px 20px 8px}
.lc-article h1{font-size:22px;line-height:1.3;margin:0 0 16px}
.lc-md{overflow-wrap:anywhere}
.lc-md p{margin:0 0 12px}
.lc-md h1,.lc-md h2,.lc-md h3,.lc-md h4{margin:20px 0 8px;line-height:1.3}
.lc-md h2{font-size:18px}.lc-md h3{font-size:16px}.lc-md h4{font-size:15px}
.lc-md ul,.lc-md ol{margin:0 0 12px;padding-left:22px}
.lc-md li{margin:4px 0}
.lc-md a{color:var(--lc-primary)}
.lc-md code{background:var(--lc-surface-2);padding:1px 5px;border-radius:5px;font-size:.9em;font-family:ui-monospace,SFMono-Regular,Menlo,monospace}
.lc-md pre{background:var(--lc-surface-2);padding:12px;border-radius:10px;overflow-x:auto;margin:0 0 12px}
.lc-md pre code{background:none;padding:0}
.lc-md blockquote{margin:0 0 12px;padding:4px 0 4px 12px;border-left:3px solid var(--lc-border);color:var(--lc-muted)}
.lc-md img{max-width:100%;border-radius:10px}
.lc-md hr{border:0;border-top:1px solid var(--lc-border);margin:16px 0}
.lc-md>:last-child{margin-bottom:0}
.lc-feedback{margin:8px 20px 16px;padding:16px;border-radius:14px;background:var(--lc-surface);text-align:center}
.lc-feedback-btns{display:flex;justify-content:center;gap:8px;margin-top:10px}
.lc-pill{padding:8px 18px;border-radius:999px;border:1px solid var(--lc-border)!important;background:var(--lc-bg)!important;font-weight:500}
.lc-pill:hover{border-color:var(--lc-primary)!important}
.lc-still{margin:0 20px 24px;display:flex;align-items:center;justify-content:space-between;gap:12px}
.lc-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;padding:10px 16px;border-radius:12px;
  background:var(--lc-primary)!important;color:var(--lc-on-primary)!important;font-weight:600}
.lc-btn:disabled{opacity:.5;cursor:default}
.lc-btn svg{width:18px;height:18px}

.lc-thread{display:flex;flex-direction:column;gap:2px;padding:16px 12px}
.lc-older{align-self:center;font-size:13px;color:var(--lc-primary);padding:6px 10px;margin-bottom:8px}
.lc-msg{display:flex;gap:8px;align-items:flex-end;max-width:85%}
.lc-msg.lc-mine{align-self:flex-end;flex-direction:row-reverse}
.lc-msg+.lc-msg{margin-top:2px}
.lc-msg.lc-first{margin-top:10px}
.lc-msg .lc-avatar{width:28px;height:28px;font-size:12px}
.lc-msg .lc-avatar.lc-hidden{visibility:hidden}
.lc-bubble-wrap{display:flex;flex-direction:column;min-width:0}
.lc-mine .lc-bubble-wrap{align-items:flex-end}
.lc-author{font-size:12px;color:var(--lc-muted);margin:0 0 3px 4px}
.lc-bubble{padding:9px 13px;border-radius:18px;background:var(--lc-surface);white-space:pre-wrap;overflow-wrap:anywhere}
.lc-mine .lc-bubble{background:var(--lc-primary);color:var(--lc-on-primary)}
.lc-mine .lc-bubble a{color:inherit}
.lc-bubble .lc-md p{margin:0}
.lc-bubble.lc-pending{opacity:.6}
.lc-bubble.lc-failed{background:none;border:1px solid var(--lc-danger);color:var(--lc-text)}
.lc-meta{font-size:12px;color:var(--lc-muted);margin:3px 6px 0}
.lc-meta.lc-danger{color:var(--lc-danger)}
.lc-system{align-self:center;font-size:12px;color:var(--lc-muted);margin:12px 0;text-align:center;max-width:85%}
.lc-system.lc-auto{background:var(--lc-surface);padding:10px 14px;border-radius:12px;font-size:13px;color:var(--lc-text);white-space:pre-wrap}
.lc-typing{display:inline-flex;gap:4px;padding:12px 14px;border-radius:18px;background:var(--lc-surface)}
.lc-typing i{width:6px;height:6px;border-radius:50%;background:var(--lc-muted);animation:lc-blink 1.2s infinite both}
.lc-typing i:nth-child(2){animation-delay:.2s}.lc-typing i:nth-child(3){animation-delay:.4s}
@keyframes lc-blink{0%,80%,100%{opacity:.3}40%{opacity:1}}
.lc-attachments{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px}
.lc-mine .lc-attachments{justify-content:flex-end}
.lc-attachment-img{display:block;max-width:220px;max-height:220px;border-radius:12px;object-fit:cover;background:var(--lc-surface-2)}
.lc-attachment-file{display:flex;align-items:center;gap:8px;padding:8px 12px;border-radius:12px;background:var(--lc-surface-2);color:inherit;text-decoration:none;font-size:13px}
.lc-attachment-file svg{width:18px;height:18px}

.lc-banner{padding:8px 12px;font-size:13px;text-align:center;background:var(--lc-surface);color:var(--lc-muted)}
.lc-suggest{border-top:1px solid var(--lc-border);padding:8px 0 0}
.lc-suggest .lc-card-title{padding:4px 16px}
.lc-suggest .lc-row{border-top:0;padding:8px 16px}
.lc-composer{border-top:1px solid var(--lc-border);padding:10px 10px 12px;display:flex;flex-direction:column;gap:8px}
.lc-composer-row{display:flex;align-items:flex-end;gap:6px}
.lc-composer textarea{flex:1;resize:none;border:1px solid var(--lc-border);border-radius:14px;padding:10px 12px;font:inherit;color:inherit;
  background:var(--lc-bg);max-height:140px;min-height:42px;line-height:1.4}
.lc-composer textarea:focus{outline:none;border-color:var(--lc-primary)}
.lc-send{width:42px;height:42px;border-radius:12px;background:var(--lc-primary)!important;color:var(--lc-on-primary)!important;display:grid;place-items:center;flex:none}
.lc-send:disabled{opacity:.4;cursor:default}
.lc-send svg{width:20px;height:20px}
.lc-chips{display:flex;flex-wrap:wrap;gap:6px}
.lc-chip{display:flex;align-items:center;gap:6px;padding:4px 6px 4px 10px;border-radius:999px;background:var(--lc-surface);font-size:13px;max-width:100%}
.lc-chip span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:160px}
.lc-chip button{width:20px;height:20px;border-radius:50%;display:grid;place-items:center}
.lc-chip button svg{width:14px;height:14px}
.lc-chip.lc-uploading{opacity:.6}
.lc-chip.lc-chip-error{background:none;border:1px solid var(--lc-danger);color:var(--lc-danger)}
.lc-chip.lc-chip-error span{white-space:normal;max-width:none}

.lc-csat{align-self:stretch;margin:12px 8px;padding:16px;border-radius:14px;background:var(--lc-surface);text-align:center}
.lc-csat-scores{display:flex;justify-content:center;gap:6px;margin:12px 0}
.lc-csat-scores button{width:44px;height:44px;border-radius:12px;font-size:22px;background:var(--lc-bg)!important;border:1px solid var(--lc-border)!important;transition:transform .1s}
.lc-csat-scores button:hover,.lc-csat-scores button[aria-checked="true"]{border-color:var(--lc-primary)!important;transform:scale(1.08)}
.lc-csat textarea{width:100%;border:1px solid var(--lc-border);border-radius:10px;padding:8px 10px;font:inherit;color:inherit;background:var(--lc-bg);resize:vertical;min-height:60px;margin-bottom:10px}
`;
var INJECTED = /* @__PURE__ */ new WeakSet();
function injectStyles(root = document) {
  if (INJECTED.has(root)) return;
  INJECTED.add(root);
  const style = document.createElement("style");
  style.setAttribute("data-livechat", "");
  style.textContent = MESSENGER_CSS;
  ("head" in root && root.head || root).appendChild(style);
}

// src/web/Messenger.tsx
import { Fragment as Fragment6, jsx as jsx8, jsxs as jsxs7 } from "react/jsx-runtime";
function useThemeStyle(primaryColor) {
  const config = useWorkspaceConfig();
  const primary = primaryColor ?? config?.branding.primaryColor ?? "#4F46E5";
  return { "--lc-primary": primary, "--lc-on-primary": onColor(primary) };
}
function useStyles(styleRoot) {
  useInsertionEffect(() => {
    if (typeof document !== "undefined") injectStyles(styleRoot ?? document);
  }, [styleRoot]);
}
function deepActiveElement() {
  let el = document.activeElement;
  while (el?.shadowRoot?.activeElement) el = el.shadowRoot.activeElement;
  return el;
}
function WithHeader({ children }) {
  const t = useTranslate();
  return /* @__PURE__ */ jsxs7(Fragment6, { children: [
    /* @__PURE__ */ jsx8(Header, { title: t("home.title"), focusTarget: false }),
    children
  ] });
}
function Screens() {
  const { route } = useMessenger();
  const status = useLiveChatState((s) => s.status);
  const client = useLiveChatClient();
  if (status === "error") return /* @__PURE__ */ jsx8(WithHeader, { children: /* @__PURE__ */ jsx8(ErrorState, { onRetry: () => void client.init().catch(() => {
  }) }) });
  if (status !== "ready") return /* @__PURE__ */ jsx8(WithHeader, { children: /* @__PURE__ */ jsx8(Loading, {}) });
  switch (route.name) {
    case "home":
      return /* @__PURE__ */ jsx8(HomeScreen, {});
    case "conversations":
      return /* @__PURE__ */ jsx8(ConversationsScreen, {});
    case "conversation":
      return /* @__PURE__ */ jsx8(ChatScreen, { conversationId: route.id }, route.id);
    case "new":
      return /* @__PURE__ */ jsx8(ChatScreen, { conversationId: null }, "new");
    case "category":
      return /* @__PURE__ */ jsx8(CategoryScreen, { id: route.id, title: route.title });
    case "article":
      return /* @__PURE__ */ jsx8(ArticleScreen, { slug: route.slug }, route.slug);
    case "search":
      return /* @__PURE__ */ jsx8(HomeScreen, {});
  }
}
function Messenger({ inline, primaryColor, theme, className, styleRoot }) {
  useStyles(styleRoot);
  const messenger = useMessenger();
  const t = useTranslate();
  const style = useThemeStyle(primaryColor);
  const panel = useRef2(null);
  const client = useLiveChatClient();
  useEffect2(() => {
    if (typeof document === "undefined") return;
    const sync = () => {
      if (document.visibilityState === "visible") {
        client.resume();
        client.startWatching();
      } else {
        client.stopWatching();
      }
    };
    if (document.visibilityState === "visible") client.startWatching();
    document.addEventListener("visibilitychange", sync);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      client.stopWatching();
    };
  }, [client]);
  useEffect2(() => {
    if (inline || !messenger.isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && panel.current && e.composedPath().includes(panel.current)) messenger.close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [inline, messenger]);
  const isOpen = inline || messenger.isOpen;
  useEffect2(() => {
    if (inline || !messenger.isOpen) return;
    const previous = deepActiveElement();
    const root = panel.current?.getRootNode();
    panel.current?.focus();
    return () => {
      const active = deepActiveElement();
      if (active && active !== document.body && active.isConnected && !panel.current?.contains(active)) return;
      if (previous?.isConnected && previous !== document.body) previous.focus();
      else root?.querySelector(".lc-launcher")?.focus();
    };
  }, [inline, messenger.isOpen]);
  const firstRoute = useRef2(true);
  useEffect2(() => {
    if (firstRoute.current) {
      firstRoute.current = false;
      return;
    }
    const root = panel.current;
    if (!root) return;
    const active = deepActiveElement();
    if (active && active !== root && root.contains(active)) return;
    (root.querySelector("[data-screen-title]") ?? root).focus();
  }, [messenger.route]);
  if (!isOpen) return null;
  return /* @__PURE__ */ jsx8("div", { className: `lc-root${className ? ` ${className}` : ""}`, "data-theme": theme, style, children: /* @__PURE__ */ jsx8(
    "div",
    {
      ref: panel,
      tabIndex: -1,
      className: `lc-panel${inline ? " lc-inline" : ""}`,
      role: inline ? "region" : "dialog",
      "aria-label": t("home.title"),
      children: /* @__PURE__ */ jsx8(InlineContext.Provider, { value: !!inline, children: /* @__PURE__ */ jsx8(
        MessengerErrorBoundary,
        {
          fallback: (reset) => /* @__PURE__ */ jsx8(WithHeader, { children: /* @__PURE__ */ jsx8(ErrorState, { onRetry: reset }) }),
          children: /* @__PURE__ */ jsx8(Screens, {})
        },
        JSON.stringify(messenger.route)
      ) })
    }
  ) });
}
function Launcher({ primaryColor, theme, styleRoot }) {
  useStyles(styleRoot);
  const messenger = useMessenger();
  const unread = useUnreadCount();
  const t = useTranslate();
  const style = useThemeStyle(primaryColor);
  const client = useLiveChatClient();
  useEffect2(() => {
    const id = setInterval(() => {
      if (typeof document === "undefined" || document.visibilityState === "visible") void client.refreshUnread();
    }, 6e4);
    return () => clearInterval(id);
  }, [client]);
  return /* @__PURE__ */ jsx8("div", { className: "lc-root", "data-theme": theme, style, children: /* @__PURE__ */ jsxs7(
    "button",
    {
      type: "button",
      className: `lc-launcher${messenger.isOpen ? " lc-open" : ""}`,
      onClick: messenger.toggle,
      "aria-label": messenger.isOpen ? t("common.close") : unread > 0 ? t("launcher.unread", { title: t("home.title"), count: unread }) : t("home.title"),
      "aria-expanded": messenger.isOpen,
      children: [
        messenger.isOpen ? /* @__PURE__ */ jsx8(CloseIcon, {}) : /* @__PURE__ */ jsx8(ChatIcon, {}),
        !messenger.isOpen && unread > 0 && /* @__PURE__ */ jsx8("span", { className: "lc-badge", "aria-hidden": "true", children: unread > 99 ? "99+" : unread })
      ]
    }
  ) });
}
function LiveChatWidget({ hideLauncher, ...props }) {
  return /* @__PURE__ */ jsxs7(Fragment6, { children: [
    /* @__PURE__ */ jsx8(Messenger, { ...props }),
    !hideLauncher && /* @__PURE__ */ jsx8(Launcher, { ...props })
  ] });
}

// src/index.ts
export * from "@kobecuppens/livechat-core";
export {
  Launcher,
  LiveChatProvider,
  LiveChatWidget,
  MESSENGER_CSS,
  Markdown,
  Messenger,
  injectStyles,
  useArticle,
  useArticleSuggestions,
  useCategoryArticles,
  useConversation,
  useConversations,
  useHelpHome,
  useHelpSearch,
  useLiveChatClient,
  useLiveChatState,
  useMessenger,
  useTranslate,
  useUnreadCount,
  useWorkspaceConfig
};
